import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Plus, ListFilter, Users, MoreVertical, Loader2 } from "lucide-react";
import { useState } from "react";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useShortlists } from "@/hooks/useShortlists";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";

export default function EmpresaShortlists() {
  const navigate = useNavigate();
  const { orgId } = usePartnerContext();
  const { shortlists, isLoading, createShortlist } = useShortlists(orgId);
  const [newName, setNewName] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createShortlist.mutateAsync(newName.trim());
      toast({ title: "Shortlist criada!" });
      setNewName("");
      setShowCreate(false);
    } catch (err: any) {
      toast({ title: "Erro ao criar", description: err.message, variant: "destructive" });
    }
  };

  if (isLoading) {
    return <div className="page-container-desktop flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="page-container-desktop">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Shortlists</h1>
          <p className="text-muted-foreground mt-1">Organize talentos em listas para suas vagas</p>
        </div>
        <Button variant="hero" onClick={() => setShowCreate(true)}><Plus className="w-5 h-5" />Nova Shortlist</Button>
      </motion.div>

      {showCreate && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-6 border border-border mb-6">
          <h3 className="font-heading font-semibold text-foreground mb-4">Nova Shortlist</h3>
          <div className="flex gap-3">
            <Input placeholder="Nome da shortlist" value={newName} onChange={(e) => setNewName(e.target.value)} />
            <Button variant="hero" onClick={handleCreate} disabled={!newName.trim()}>Criar</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
          </div>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shortlists.map((list) => (
          <div key={list.id} className="card-interactive">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <ListFilter className="w-5 h-5 text-primary" />
              </div>
              <Button variant="ghost" size="icon-sm"><MoreVertical className="w-4 h-4" /></Button>
            </div>
            <h3 className="font-heading font-semibold text-foreground mb-1">{list.name}</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Users className="w-4 h-4" />
              <span>{(list.shortlist_items as any[])?.length ?? 0} talentos</span>
            </div>
            <div className="text-xs text-muted-foreground pt-4 border-t border-border">
              <p>Criada em: {format(new Date(list.created_at), "dd/MM/yyyy")}</p>
            </div>
            <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate(`/empresa/shortlists/${list.id}`)}>Ver Talentos</Button>
          </div>
        ))}

        {!showCreate && (
          <div onClick={() => setShowCreate(true)}
            className="border-2 border-dashed border-border rounded-2xl p-6 flex flex-col items-center justify-center text-center min-h-[200px] hover:border-primary/50 transition-colors cursor-pointer">
            <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-3">
              <Plus className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium text-foreground">Criar nova shortlist</p>
            <p className="text-sm text-muted-foreground mt-1">Organize talentos para suas vagas</p>
          </div>
        )}
      </motion.div>

      {shortlists.length === 0 && !showCreate && (
        <div className="bg-muted/50 rounded-2xl p-12 text-center">
          <ListFilter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">Nenhuma shortlist criada</h3>
          <p className="text-muted-foreground mb-4">Crie shortlists para organizar talentos por vaga ou perfil</p>
          <Button variant="accent" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" />Criar primeira shortlist</Button>
        </div>
      )}
    </div>
  );
}
